"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "motion/react";
import { type ReactNode, useEffect, useRef, useState } from "react";

const MIN_POSITION = 6;
const MAX_POSITION = 94;

/** Scripted one-time sweep that demonstrates the reveal: lean into the "after"
 * side, drift back past centre, then settle just left of middle. */
const DEMO_KEYFRAMES = [24, 64, 48];
const DEMO_SEGMENT_MS = 700;
const DEMO_START_DELAY_MS = 450;

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

interface BeforeAfterCompareProps {
  /** Base panel, fully visible on the left of the divider. */
  before: ReactNode;
  /** Overlay panel, revealed to the right of the divider. */
  after: ReactNode;
  /** Corner pill shown over the "before" side. */
  beforeLabel: string;
  /** Corner pill shown over the "after" side. */
  afterLabel: string;
  /** Accessible description of the comparison for the range input. */
  ariaLabel: string;
  /** Divider start position, 0–100. Defaults to a slight lean towards "before". */
  initialPosition?: number;
}

/**
 * Before/after drag slider. The two panels share a single grid cell, so the
 * card sizes to the taller of the two and both stretch to equal height; the
 * "after" panel is then revealed from the right with a `clipPath` wipe. The
 * interaction layer is a full-bleed range input, so it is draggable, touchable
 * and keyboard-operable for free.
 *
 * To make the gesture obvious for non-technical visitors, the handle carries a
 * cluster of hints that all retire on first interaction (and collapse to a
 * clean static frame under `prefers-reduced-motion`): a one-time auto-demo
 * sweep when the card scrolls into view, outward chevrons on the handle, a
 * pulsing ring, and a floating "Drag" label.
 */
export function BeforeAfterCompare({
  before,
  after,
  beforeLabel,
  afterLabel,
  ariaLabel,
  initialPosition = 55,
}: BeforeAfterCompareProps) {
  const [position, setPosition] = useState(initialPosition);
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const reduced = !!useReducedMotion();
  const inView = useInView(containerRef, { once: true, amount: 0.6 });
  const showHint = !hasInteracted;

  // One-time auto-demo: when the card scrolls into view, glide the divider
  // through a scripted sweep so the visitor sees the wipe happen by itself.
  useEffect(() => {
    if (!inView || hasInteracted || reduced) return;

    const keyframes = [initialPosition, ...DEMO_KEYFRAMES];
    const total = DEMO_SEGMENT_MS * (keyframes.length - 1);
    let raf = 0;
    let startTime = 0;

    const tick = (now: number) => {
      if (!startTime) startTime = now;
      const elapsed = Math.min(now - startTime, total);
      const segment = Math.min(Math.floor(elapsed / DEMO_SEGMENT_MS), keyframes.length - 2);
      const localT = (elapsed - segment * DEMO_SEGMENT_MS) / DEMO_SEGMENT_MS;
      const eased = easeInOut(localT);
      setPosition(keyframes[segment] + (keyframes[segment + 1] - keyframes[segment]) * eased);
      if (elapsed < total) raf = requestAnimationFrame(tick);
    };

    const timer = setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, DEMO_START_DELAY_MS);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [inView, hasInteracted, reduced, initialPosition]);

  return (
    <div
      ref={containerRef}
      className="relative grid select-none overflow-hidden rounded-2xl border border-border shadow-xl"
    >
      {/* Before — base layer */}
      <div className="col-start-1 row-start-1 overflow-hidden" aria-hidden="true">
        {before}
      </div>

      {/* After — revealed from the right */}
      <div
        className="col-start-1 row-start-1 overflow-hidden"
        style={{ clipPath: `inset(0 0 0 ${position}%)` }}
        aria-hidden="true"
      >
        {after}
      </div>

      {/* Corner labels */}
      <span
        className="pointer-events-none absolute left-3 top-3 z-10 rounded-full bg-foreground/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-background transition-opacity duration-200"
        style={{ opacity: position > 18 ? 1 : 0 }}
        aria-hidden="true"
      >
        {beforeLabel}
      </span>
      <span
        className="pointer-events-none absolute right-3 top-3 z-10 rounded-full bg-ink px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-background transition-opacity duration-200"
        style={{ opacity: position < 82 ? 1 : 0 }}
        aria-hidden="true"
      >
        {afterLabel}
      </span>

      {/* Divider + handle + drag hints */}
      <div
        className="pointer-events-none absolute inset-y-0 z-10"
        style={{ left: `${position}%` }}
        aria-hidden="true"
      >
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-ink/80" />

        {/* Floating "Drag" label — fades in above the handle, retires on use */}
        <AnimatePresence>
          {showHint && (
            <motion.div
              key="drag-hint"
              className="absolute left-0 z-20"
              style={{ bottom: "calc(50% + 1.55rem)" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              <div className="-translate-x-1/2">
                <motion.span
                  className="flex items-center gap-0.5 whitespace-nowrap rounded-full bg-ink px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-background shadow-md"
                  animate={reduced ? undefined : { x: [-3, 3, -3] }}
                  transition={
                    reduced
                      ? undefined
                      : { duration: 1.6, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }
                  }
                >
                  <ChevronLeft className="h-3 w-3" strokeWidth={2.5} />
                  Drag
                  <ChevronRight className="h-3 w-3" strokeWidth={2.5} />
                </motion.span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Handle cluster, centred on the divider */}
        <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="relative flex h-10 w-10 items-center justify-center">
            {/* Pulsing ring — draws the eye until first interaction */}
            {showHint && !reduced && (
              <span className="absolute inset-0 -z-10 rounded-full bg-ink/40 animate-ping" />
            )}
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-background shadow-lg ring-2 ring-background/70">
              <ChevronLeft className="-mr-0.5 h-4 w-4" strokeWidth={2.5} />
              <ChevronRight className="-ml-0.5 h-4 w-4" strokeWidth={2.5} />
            </span>
          </div>
        </div>
      </div>

      {/* Interaction layer — full-bleed range input */}
      <input
        type="range"
        min={MIN_POSITION}
        max={MAX_POSITION}
        step={1}
        value={position}
        onPointerDown={() => setHasInteracted(true)}
        onKeyDown={() => setHasInteracted(true)}
        onChange={(event) => {
          setHasInteracted(true);
          setPosition(Number(event.target.value));
        }}
        aria-label={ariaLabel}
        className="absolute inset-0 z-20 h-full w-full cursor-ew-resize appearance-none bg-transparent opacity-0"
      />
    </div>
  );
}
